import { Box, Button, Flex, Grid, HStack, Stack, Text } from "@chakra-ui/react";
import { useCustom, useList } from "@refinedev/core";
import { Activity, Bot, BrainCircuit, CircleDollarSign, Clock3, Database, Gauge, Layers3, ListChecks, Radio, ShieldCheck, Target, WalletCards } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { AppSelect } from "../components/app-select";
import { RuntimeActivityPill, runtimeHeartbeatAge } from "../components/runtime-activity";
import { DataBar, EmptyState, MetricCard, Money, PageHeader, Panel, StatusPill } from "../components/ui";
import type { BotRecord, PositionRecord } from "../types/domain";

type PortfolioSummary = { equity: number; availableEquity: number; todayPnl: number; currentDrawdown: number; grossExposure: number; netExposure: number; marginUsed: number; marginUtilization: number; positionCount: number; accountCount: number; maxLeverage: number; maxDrawdownLimit: number; maxDailyLossLimit: number; maxSessionCloseLossLimit: number; asOf: string };

export function PortfolioPage() {
  const [accountScope, setAccountScope] = useState("all");
  const summaryQuery = useCustom<PortfolioSummary>({ url: "/portfolio/summary", method: "get", queryOptions: { refetchInterval: 5_000 } });
  const positionsQuery = useList<PositionRecord>({ resource: "positions", pagination: { currentPage: 1, pageSize: 10_000 }, liveMode: "auto" });
  const botsQuery = useList<BotRecord>({ resource: "bots", pagination: { currentPage: 1, pageSize: 10_000 }, liveMode: "auto" });
  const allPositions = positionsQuery.result.data ?? [];
  const bots = botsQuery.result.data ?? [];
  const summary = summaryQuery.query.data?.data;
  const selectedBot = bots.find((bot) => bot.id === accountScope);
  const selectedAccount = selectedBot?.runtime?.lastKnownAccount;
  const positions = selectedBot
    ? allPositions.filter((position) => position.bot === selectedBot.name)
    : allPositions;
  const liveBots = bots.filter((bot) => bot.mode === "LIVE");
  const hasLive = liveBots.some((bot) => bot.status === "RUNNING");
  const isAggregate = !selectedBot;
  const equity = isAggregate ? summary?.equity : selectedAccount?.equity;
  const availableEquity = isAggregate ? summary?.availableEquity : selectedAccount?.availableFunds;
  const todayPnl = isAggregate ? summary?.todayPnl : selectedBot.pnlToday;
  const grossExposure = isAggregate ? summary?.grossExposure : selectedBot.exposure;
  const netExposure = isAggregate
    ? summary?.netExposure
    : selectedBot.position === "FLAT"
      ? 0
      : selectedBot.position.toUpperCase().includes("SHORT")
        ? -selectedBot.exposure
        : selectedBot.exposure;
  const marginUtilization = isAggregate
    ? summary?.marginUtilization
    : selectedAccount?.equity && selectedAccount.marginUsed !== undefined
      ? selectedAccount.marginUsed / selectedAccount.equity * 100
      : undefined;
  const highWater = selectedBot?.runtime?.highWaterEquity;
  const drawdown = isAggregate
    ? summary?.currentDrawdown
    : highWater && equity !== undefined && highWater > 0
      ? Math.max(0, (highWater - equity) / highWater * 100)
      : undefined;
  const maxLeverage = isAggregate ? summary?.maxLeverage : selectedBot.safety?.maxLeverage;
  const maxDrawdown = isAggregate ? summary?.maxDrawdownLimit : selectedBot.safety?.maxDrawdownPct !== undefined ? selectedBot.safety.maxDrawdownPct * 100 : undefined;
  const maxDailyLoss = isAggregate ? summary?.maxDailyLossLimit : selectedBot.safety?.maxDailyLossPct !== undefined ? selectedBot.safety.maxDailyLossPct * 100 : undefined;
  const maxSessionCloseLoss = isAggregate ? summary?.maxSessionCloseLossLimit : selectedBot.safety?.maxSessionCloseLossPct !== undefined ? selectedBot.safety.maxSessionCloseLossPct * 100 : undefined;
  const dayStart = selectedBot?.runtime?.dayStartEquity;
  const dailyLoss = todayPnl !== undefined && todayPnl < 0
    ? -todayPnl / Math.max(1, dayStart ?? ((equity ?? 0) - todayPnl)) * 100
    : 0;
  const paperBots = bots.filter((bot) => bot.executionType === "SIMULATED");
  const riskBreached = [
    [grossExposure, maxLeverage],
    [marginUtilization, 100],
    [drawdown, maxDrawdown],
    [dailyLoss, maxDailyLoss],
  ].some(([value, limit]) => value !== undefined && limit !== undefined && value > limit);
  const scopeLabel = selectedBot ? selectedBot.name : `All execution accounts · ${bots.length}`;

  return (
    <Stack gap="5">
      <PageHeader
        title="Portfolio Accounts"
        description="Persisted balances, allocation and risk across trading-kernel execution accounts."
        actions={
          <>
            <StatusPill tone={hasLive ? "negative" : "warning"}>
              {hasLive ? "LIVE EXECUTION ACTIVE" : liveBots.length > 0 ? `${liveBots.length} LIVE ACCOUNTS IDLE` : `${paperBots.length} PAPER LEDGERS`}
            </StatusPill>
            <AppSelect
              ariaLabel="Select portfolio account scope"
              value={selectedBot?.id ?? "all"}
              width={{ base: "100%", md: "330px" }}
              options={[
                { value: "all", label: `All execution accounts · ${bots.length}` },
                ...bots.map((bot) => ({ value: bot.id, label: bot.name })),
              ]}
              onValueChange={setAccountScope}
            />
          </>
        }
      />

      {paperBots.length === bots.length && bots.length > 0 && (
        <Flex
          bg="rgba(245,158,11,.07)"
          borderWidth="1px"
          borderColor="rgba(245,158,11,.24)"
          borderRadius="4px"
          px={{ base: "4", md: "5" }}
          py="4"
          gap="3"
          align={{ base: "start", md: "center" }}
          direction={{ base: "column", md: "row" }}
        >
          <Flex boxSize="36px" flexShrink="0" align="center" justify="center" borderRadius="4px" bg="rgba(245,158,11,.13)" color="status.warning">
            <Box as={WalletCards} boxSize="4.5" />
          </Flex>
          <Box flex="1">
            <Text fontSize="sm" fontWeight="750">Why only paper balances?</Text>
            <Text mt="1" fontSize="xs" color="fg.muted" lineHeight="1.6">
              Both registered bots use PAPER / SIMULATED execution. cTrader DEMO supplies broker market data, but these balances are isolated strategy ledgers—not the cTrader demo account balance.
            </Text>
          </Box>
          <HStack gap="2" flexWrap="wrap">
            <StatusPill tone="warning">SIMULATED</StatusPill>
            <StatusPill>CTRADER DEMO DATA</StatusPill>
            <StatusPill tone="neutral">NO BROKER BALANCE</StatusPill>
          </HStack>
        </Flex>
      )}

      <Flex align="center" justify="space-between" gap="3" flexWrap="wrap">
        <Box>
          <Text fontSize="10px" color="fg.subtle" textTransform="uppercase" letterSpacing=".06em">Current scope</Text>
          <Text mt="1" fontSize="sm" fontWeight="750">{scopeLabel}</Text>
        </Box>
        <StatusPill tone={selectedBot?.mode === "LIVE" ? "negative" : "info"}>
          {selectedBot ? `${selectedBot.mode} · ${selectedBot.executionType ?? "UNKNOWN"} · ${selectedBot.brokerEnvironment ?? "UNKNOWN"}` : `${summary?.accountCount ?? 0} ACCOUNT SNAPSHOTS`}
        </StatusPill>
      </Flex>

      <Grid templateColumns={{ base: "1fr 1fr", lg: "repeat(4,1fr)" }} gap="3">
        <MetricCard icon={WalletCards} label={isAggregate ? "Ledger equity" : "Account equity"} value={equity === undefined ? "—" : <Money value={equity} />} detail={isAggregate ? `Aggregate of ${summary?.accountCount ?? 0} persisted ledgers` : selectedBot.executionType === "SIMULATED" ? "Paper ledger · not broker cash" : "Broker execution account"} />
        <MetricCard icon={CircleDollarSign} label="Available funds" value={availableEquity === undefined ? "—" : <Money value={availableEquity} />} detail={marginUtilization === undefined ? "Waiting for account snapshot" : `${marginUtilization.toFixed(2)}% margin utilization`} />
        <MetricCard icon={Activity} label="PnL today" value={todayPnl === undefined ? "—" : <Money value={todayPnl} signed />} detail={isAggregate ? "Across selected account scope" : selectedBot.status} />
        <MetricCard icon={Gauge} label="Gross exposure" value={grossExposure === undefined ? "—" : `${grossExposure.toFixed(3)}x`} detail={netExposure === undefined ? "Waiting for account snapshot" : `Net ${netExposure.toFixed(3)}x`} />
      </Grid>

      <Grid templateColumns={{ base: "1fr", lg: "minmax(0,1.45fr) minmax(300px,.65fr)" }} gap="4" alignItems="start">
        <Panel icon={Layers3} title="Open allocation" action={<StatusPill>{positions.length} POSITIONS</StatusPill>}>
          {positions.length === 0 && !positionsQuery.query.isLoading ? (
            <EmptyState
              icon={ShieldCheck}
              title="No open allocation"
              description={`${scopeLabel} is currently flat. Positions will appear here only after an execution account persists an open broker or paper position.`}
            />
          ) : (
            <Stack gap="3">
              {positions.map((position) => <Sleeve key={position.id} name={`${position.bot} · ${position.symbol}`} description={`${position.side} ${position.quantity} lots · entry ${position.entry.toFixed(2)} · mark ${position.mark.toFixed(2)}`} exposure={`${position.exposure.toFixed(3)}x`} contribution={position.pnl} />)}
            </Stack>
          )}
          <Box pt="3" mt="3" borderTopWidth="1px" borderColor="border.default">
            <Flex justify="space-between" gap="3">
              <Text fontSize="xs" fontWeight="700">Scope gross / net</Text>
              <Text fontFamily="mono" fontWeight="800">{grossExposure === undefined || netExposure === undefined ? "—" : `${grossExposure.toFixed(3)}x / ${netExposure.toFixed(3)}x`}</Text>
            </Flex>
          </Box>
        </Panel>
        <Panel icon={ShieldCheck} title="Risk limits" action={<StatusPill tone={riskBreached ? "negative" : "positive"}>{riskBreached ? "BREACHED" : "SAFE"}</StatusPill>}>
          <Stack gap="4">
            <PortfolioRiskLine label="Gross exposure" value={grossExposure} max={maxLeverage} unit="x" />
            <PortfolioRiskLine label="Margin utilization" value={marginUtilization} max={100} unit="%" />
            <PortfolioRiskLine label="Current drawdown" value={drawdown} max={maxDrawdown} unit="%" />
            <PortfolioRiskLine label="Hard intraday loss" value={dailyLoss} max={maxDailyLoss} unit="%" />
            {(maxSessionCloseLoss ?? 0) > 0 && <PortfolioRiskLine label="Session-close loss" value={dailyLoss} max={maxSessionCloseLoss} unit="%" />}
          </Stack>
        </Panel>
      </Grid>

      <Panel icon={Database} title="Execution accounts" action={<StatusPill>{bots.length} ACCOUNTS</StatusPill>}>
        <Grid templateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap="3">
          {bots.map((bot) => (
            <ExecutionAccountCard
              key={bot.id}
              bot={bot}
              selected={selectedBot?.id === bot.id}
              onSelect={() => setAccountScope(bot.id)}
            />
          ))}
        </Grid>
        {bots.length === 0 && <Text color="fg.muted" fontSize="xs">{botsQuery.query.isLoading ? "Loading execution accounts…" : "No registered execution accounts"}</Text>}
      </Panel>
    </Stack>
  );
}

function PortfolioRiskLine({ label, value, max, unit }: { label: string; value?: number; max?: number; unit: string }) {
  const ready = value !== undefined && max !== undefined;
  return (
    <Box>
      <Flex justify="space-between" gap="3" mb="2">
        <Text fontSize="xs" color="fg.muted">{label}</Text>
        <Text fontFamily="mono" fontSize="xs" fontWeight="750">{ready ? `${value.toFixed(2)}${unit} / ${max.toFixed(2)}${unit}` : "—"}</Text>
      </Flex>
      <DataBar value={value ?? 0} max={max ?? 1} tone={ready && value / Math.max(max, 0.0001) > 0.8 ? "warning" : "positive"} />
    </Box>
  );
}

function ExecutionAccountCard({ bot, selected, onSelect }: { bot: BotRecord; selected: boolean; onSelect: () => void }) {
  const account = bot.runtime?.lastKnownAccount;
  const isSimulated = bot.executionType === "SIMULATED";
  return (
    <Box borderWidth="1px" borderColor={selected ? "brand.500" : "border.default"} borderRadius="4px" bg={selected ? "rgba(99,102,241,.06)" : "bg.subtle"} p="4" transition="border-color .15s ease" _hover={{ borderColor: "border.strong" }}>
        <Flex justify="space-between" align="start" gap="3">
          <Box minW="0">
            <Text fontSize="sm" fontWeight="800" truncate>{bot.name}</Text>
            <Text mt="1" fontSize="10px" fontFamily="mono" color="fg.subtle" truncate>{account?.accountId ?? bot.id}</Text>
          </Box>
          <StatusPill tone={selected ? "info" : bot.status === "RUNNING" ? "positive" : "neutral"}>{selected ? "SELECTED" : bot.status}</StatusPill>
        </Flex>
        <HStack mt="3" gap="2" flexWrap="wrap">
          <StatusPill tone={bot.mode === "LIVE" ? "negative" : "warning"}>{bot.mode}</StatusPill>
          <StatusPill>{bot.executionType ?? "UNKNOWN"}</StatusPill>
          <StatusPill>{bot.brokerEnvironment ?? "UNKNOWN"}</StatusPill>
        </HStack>
        <Grid mt="3" templateColumns="repeat(3,1fr)" gap="2">
          <AccountFact label="Equity" value={account?.equity === undefined ? "—" : `$${account.equity.toLocaleString()}`} />
          <AccountFact label="PnL today" value={`${bot.pnlToday >= 0 ? "+" : ""}$${bot.pnlToday.toFixed(2)}`} />
          <AccountFact label="Exposure" value={`${bot.exposure.toFixed(3)}x`} />
        </Grid>
        <Flex mt="3" justify="space-between" gap="3" align="center">
          <Box>
            <Text fontSize="10px" color="fg.muted">{isSimulated ? "Paper ledger · not cTrader balance" : "Broker execution account"}</Text>
            <Text mt=".5" fontSize="9px" color="fg.subtle">Snapshot {formatAccountTimestamp(account?.asOfMs)}</Text>
          </Box>
          <HStack gap="1">
            <Button aria-label={`Show portfolio account ${bot.name}`} size="xs" variant={selected ? "solid" : "outline"} bg={selected ? "brand.500" : undefined} color={selected ? "white" : undefined} disabled={selected} onClick={onSelect}>{selected ? "Selected" : "View account"}</Button>
            <Button asChild size="xs" variant="ghost">
              <Link to={`/bots/${bot.id}`}><Box as={Bot} boxSize="3.5" />View bot</Link>
            </Button>
          </HStack>
        </Flex>
    </Box>
  );
}

function AccountFact({ label, value }: { label: string; value: string }) {
  return <Box bg="bg.panel" borderRadius="4px" p="2.5"><Text fontSize="9px" color="fg.subtle" textTransform="uppercase" letterSpacing=".05em">{label}</Text><Text mt="1" fontFamily="mono" fontSize="xs" fontWeight="800">{value}</Text></Box>;
}

function formatAccountTimestamp(value?: number): string {
  if (value === undefined || !Number.isFinite(value)) return "not available";
  return new Date(value).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

function Sleeve({ name, description, exposure, contribution }: { name: string; description: string; exposure: string; contribution: number }) {
  return <Flex p="3.5" borderRadius="4px" bg="bg.subtle" align="center" gap="3"><Box flex="1"><HStack gap="2"><Text fontSize="xs" fontWeight="750">{name}</Text><StatusPill tone="info">{exposure}</StatusPill></HStack><Text fontSize="10px" color="fg.muted" mt="1">{description}</Text></Box><Money value={contribution} signed/></Flex>;
}

export function SleevesPage() {
  const { result, query } = useList<BotRecord>({ resource: "bots", pagination: { currentPage: 1, pageSize: 10_000 }, liveMode: "auto", queryOptions: { refetchInterval: 2_000 } });
  const bots = result.data ?? [];
  const online = bots.filter((bot) => bot.runtimeAlive).length;
  const tracking = bots.filter((bot) => bot.marketSubscriptionActive).length;
  const paused = bots.filter((bot) => bot.status === "PAUSED").length;
  const combinedTarget = bots.reduce((sum, bot) => sum + Math.abs(strategyTarget(bot) ?? 0), 0);
  const combinedExposure = bots.reduce((sum, bot) => sum + Math.abs(bot.exposure), 0);
  const ordersToday = bots.reduce((sum, bot) => sum + (bot.runtime?.ordersToday ?? 0), 0);
  return (
    <Stack gap="5">
      <PageHeader
        title="Strategy Sleeves"
        description="Persisted per-bot targets, sleeve state and execution context from the server read model."
        actions={
          <>
            <StatusPill tone={online > 0 ? "positive" : "neutral"}>{online} ONLINE</StatusPill>
            {paused > 0 && <StatusPill>{paused} PAUSED</StatusPill>}
            <StatusPill tone="info">{bots.length} REGISTERED</StatusPill>
          </>
        }
      />
      <Grid templateColumns={{ base: "1fr 1fr", lg: "repeat(4,1fr)" }} gap="3">
        <MetricCard icon={Radio} label="Online runtimes" value={query.isLoading ? "—" : `${online} / ${bots.length}`} detail={query.isLoading ? "Reading server state" : `${tracking} market subscriptions active`} />
        <MetricCard icon={Target} label="Combined target" value={query.isLoading ? "—" : `${combinedTarget.toFixed(3)}x`} detail="Sum of absolute persisted targets" />
        <MetricCard icon={Gauge} label="Account exposure" value={query.isLoading ? "—" : `${combinedExposure.toFixed(3)}x`} detail="Across registered bot accounts" />
        <MetricCard icon={ListChecks} label="Orders today" value={query.isLoading ? "—" : String(ordersToday)} detail="Persisted runtime counters" />
      </Grid>
      <Grid templateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap="4" alignItems="stretch">
        {bots.map((bot) => <StrategyRuntimeCard key={bot.id} bot={bot} />)}
      </Grid>
      {bots.length === 0 && (
        <Panel><EmptyState icon={Layers3} title={query.isLoading ? "Loading strategy runtimes" : "No strategy runtimes"} description={query.isLoading ? "Reading persisted bot state from the server." : "Create a bot to register its strategy runtime state."} /></Panel>
      )}
    </Stack>
  );
}

function StrategyRuntimeCard({ bot }: { bot: BotRecord }) {
  const isMulti = bot.strategy === "xau-multi-edge-portfolio-v1";
  const state = stateRecord(bot.runtime?.strategyState);
  const momentum = isMulti ? stateRecord(state.momentum) : state;
  const frequency = stateRecord(state.frequency);
  const target = strategyTarget(bot);
  const maxLeverage = bot.safety?.maxLeverage ?? 1;
  const accent = isMulti ? "strategy.predictive" : "status.info";
  const Icon = isMulti ? Layers3 : Activity;
  const momentumEntries = stateCounter(momentum, ["alignedEntries", "rangeEntries", "counterEntries"]);
  const momentumExits = stateCounter(momentum, ["stopExits", "scheduledExits", "failSafeExits", "stopExitCount", "scheduledExitCount", "lateFailSafeExitCount"]);
  const frequencyEntries = stateCounter(frequency, ["longEntries", "shortEntries"]);
  const frequencyExits = stateCounter(frequency, ["stopExitCount", "timedExitCount", "lateFailSafeExitCount"]);
  return (
    <Box h="100%" bg="bg.panel" borderWidth="1px" borderColor="border.default" borderRadius="panel" overflow="hidden">
      <Box h="3px" bg={accent} />
      <Stack p={{ base: "4", md: "5" }} gap="4" h="calc(100% - 3px)">
        <Flex align="start" justify="space-between" gap="3">
          <Flex gap="3" minW="0">
            <Flex boxSize="38px" flexShrink="0" align="center" justify="center" borderRadius="4px" bg="bg.subtle" color={accent}><Box as={Icon} boxSize="4.5" /></Flex>
            <Box minW="0">
              <Text fontSize="md" fontWeight="800" truncate>{isMulti ? "XAU Multi-Edge Portfolio" : "XAU Session Momentum"}</Text>
              <Text mt="1" fontSize="10px" fontFamily="mono" color="fg.subtle" truncate>{bot.strategy}</Text>
            </Box>
          </Flex>
          <StatusPill tone={bot.status === "RUNNING" ? "positive" : bot.status === "FAULTED" ? "negative" : "neutral"}>{bot.status}</StatusPill>
        </Flex>
        <HStack gap="2" flexWrap="wrap">
          <StatusPill tone="warning">{bot.mode}</StatusPill>
          <StatusPill>{bot.timeframe?.toUpperCase() ?? "—"}</StatusPill>
          <StatusPill>{bot.position}</StatusPill>
          <RuntimeActivityPill bot={bot} />
        </HStack>
        <Box p="3" borderWidth="1px" borderColor="border.subtle" borderRadius="4px" bg="bg.subtle">
          <Text fontSize="xs" color="fg.muted" lineHeight="1.6">{bot.runtimeActivityDetail ?? "No persisted runtime activity detail."}</Text>
        </Box>
        <Grid templateColumns={{ base: "1fr 1fr", md: "repeat(4,1fr)" }} gap="2">
          <RuntimeFact icon={Target} label="Target" value={target === undefined ? "Not observed" : `${target.toFixed(3)}x`} />
          <RuntimeFact icon={Gauge} label="Exposure" value={`${bot.exposure.toFixed(3)}x`} />
          <RuntimeFact icon={WalletCards} label="Equity" value={bot.runtime?.lastKnownAccount?.equity === undefined ? "—" : `$${bot.runtime.lastKnownAccount.equity.toLocaleString()}`} />
          <RuntimeFact icon={ListChecks} label="Orders" value={String(bot.runtime?.ordersToday ?? 0)} />
        </Grid>
        <Box>
          <Flex align="center" gap="2" mb="3"><Box as={isMulti ? Layers3 : Activity} boxSize="4" color={accent} /><Text fontSize="xs" fontWeight="800">{isMulti ? "Sleeve targets" : "Momentum engine"}</Text><Box h="1px" bg="border.subtle" flex="1" /></Flex>
          <Grid templateColumns={{ base: "1fr", md: isMulti ? "1fr 1fr" : "1fr" }} gap="3">
            <SleeveStateCard
              icon={Activity}
              title="Momentum"
              active={stateBoolean(momentum.active)}
              target={stateNumber(isMulti ? momentum.targetLeverage : momentum.lastTargetLeverage)}
              max={maxLeverage}
              accent="status.info"
              facts={isMulti ? [
                { label: "Signal day", value: stateText(momentum.day) },
                { label: "Entries", value: String(momentumEntries) },
                { label: "Exits", value: String(momentumExits) },
              ] : [
                { label: "Missing signal", value: String(stateNumber(momentum.skippedMissingSignal) ?? 0) },
                { label: "Below threshold", value: String(stateNumber(momentum.skippedBelowThreshold) ?? 0) },
                { label: "Exits", value: String(momentumExits) },
              ]}
            />
            {isMulti && (
              <SleeveStateCard
                icon={BrainCircuit}
                title="Range Session Frequency"
                active={stateBoolean(frequency.active)}
                target={stateNumber(frequency.lastTargetLeverage)}
                max={maxLeverage}
                accent="strategy.predictive"
                facts={[
                  { label: "Last anchor", value: stateText(frequency.lastEvaluatedAnchor) },
                  { label: "Entries", value: String(frequencyEntries) },
                  { label: "Exits", value: String(frequencyExits) },
                ]}
              />
            )}
          </Grid>
        </Box>
        <Box mt="auto">
          <Flex align="center" gap="2" mb="3"><Box as={Clock3} boxSize="4" color="fg.muted" /><Text fontSize="xs" fontWeight="800">Runtime context</Text><Box h="1px" bg="border.subtle" flex="1" /></Flex>
          <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="2">
            <ContextFact label="Runtime heartbeat" value={runtimeHeartbeatAge(bot.runtimeHeartbeat)} />
            <ContextFact label="Market session" value={(bot.marketSession ?? "UNKNOWN").replaceAll("_", " ")} />
            <ContextFact label="Last market event" value={formatRuntimeTimestamp(bot.lastMarketEvent)} />
            <ContextFact label="Account snapshot" value={formatRuntimeTimestamp(bot.runtime?.lastKnownAccount?.asOfMs)} />
          </Grid>
          <HStack mt="3" gap="2">
            <Button asChild size="sm" variant="outline"><Link to={`/bots/${bot.id}`}><Box as={Bot} boxSize="3.5" />View bot</Link></Button>
            <Button asChild size="sm" variant="ghost"><Link to={`/trading/live?bot=${encodeURIComponent(bot.id)}`}><Box as={Radio} boxSize="3.5" />Live monitor</Link></Button>
          </HStack>
        </Box>
      </Stack>
    </Box>
  );
}

function RuntimeFact({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
  return <Box p="3" bg="bg.subtle" borderRadius="4px" minW="0"><Flex align="center" gap="2"><Box as={Icon} boxSize="3.5" color="fg.muted" /><Text fontSize="9px" color="fg.subtle" textTransform="uppercase" letterSpacing=".05em" truncate>{label}</Text></Flex><Text mt="2" fontFamily="mono" fontSize="sm" fontWeight="800" truncate title={value}>{value}</Text></Box>;
}

function SleeveStateCard({ icon: Icon, title, active, target, max, accent, facts }: { icon: LucideIcon; title: string; active: boolean; target?: number; max: number; accent: string; facts: Array<{ label: string; value: string }> }) {
  return (
    <Box borderWidth="1px" borderColor="border.default" borderRadius="4px" p="3.5" bg="bg.subtle">
      <Flex justify="space-between" align="center" gap="3"><HStack gap="2"><Flex boxSize="28px" align="center" justify="center" borderRadius="4px" bg="bg.panel" color={accent}><Box as={Icon} boxSize="3.5" /></Flex><Text fontSize="xs" fontWeight="800">{title}</Text></HStack><StatusPill tone={active ? "positive" : "neutral"}>{active ? "ACTIVE" : "IDLE"}</StatusPill></Flex>
      <Flex mt="3" justify="space-between" align="baseline" gap="3"><Text fontSize="10px" color="fg.muted">Target leverage</Text><Text fontFamily="mono" fontSize="lg" fontWeight="850">{target === undefined ? "—" : `${target.toFixed(3)}x`}</Text></Flex>
      <Box mt="2"><DataBar value={Math.abs(target ?? 0)} max={max} tone={active ? "positive" : "brand"} /></Box>
      <Grid mt="3" templateColumns="repeat(3,1fr)" gap="2">{facts.map((fact) => <Box key={fact.label} minW="0"><Text fontSize="9px" color="fg.subtle" truncate>{fact.label}</Text><Text mt=".5" fontFamily="mono" fontSize="10px" fontWeight="750" truncate title={fact.value}>{fact.value}</Text></Box>)}</Grid>
    </Box>
  );
}

function ContextFact({ label, value }: { label: string; value: string }) {
  return <Flex px="3" py="2.5" borderRadius="4px" bg="bg.subtle" justify="space-between" align="center" gap="3"><Text fontSize="10px" color="fg.muted">{label}</Text><Text fontSize="10px" fontWeight="700" textAlign="right">{value}</Text></Flex>;
}

function strategyTarget(bot: BotRecord): number | undefined {
  const state = stateRecord(bot.runtime?.strategyState);
  return bot.strategy === "xau-multi-edge-portfolio-v1" ? stateNumber(state.lastAggregateTarget) : stateNumber(state.lastTargetLeverage);
}

function stateRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function stateNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function stateBoolean(value: unknown): boolean {
  return value === true;
}

function stateText(value: unknown): string {
  return value === undefined || value === null || value === "" ? "Not observed" : String(value);
}

function stateCounter(state: Record<string, unknown>, keys: string[]): number {
  return keys.reduce((total, key) => total + (stateNumber(state[key]) ?? 0), 0);
}

function formatRuntimeTimestamp(value?: string | number): string {
  if (value === undefined || value === null || value === "") return "Not observed";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Invalid timestamp" : date.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

export function RiskPage() {
  const [accountScope, setAccountScope] = useState("all");
  const summaryQuery = useCustom<PortfolioSummary>({ url: "/portfolio/summary", method: "get", queryOptions: { refetchInterval: 5_000 } });
  const botsQuery = useList<BotRecord>({ resource: "bots", pagination: { currentPage: 1, pageSize: 10_000 }, liveMode: "auto", queryOptions: { refetchInterval: 2_000 } });
  const bots = botsQuery.result.data ?? [];
  const summary = summaryQuery.query.data?.data;
  const selectedBot = bots.find((bot) => bot.id === accountScope);
  const scopeBots = selectedBot ? [selectedBot] : bots;
  const snapshots = scopeBots.map(riskSnapshot);
  const gross = worstRisk(snapshots, "gross");
  const drawdown = worstRisk(snapshots, "drawdown");
  const dailyLoss = worstRisk(snapshots, "dailyLoss");
  const margin = worstRisk(snapshots, "margin");
  const riskPoints = [gross, drawdown, dailyLoss, margin].filter((point): point is RiskPoint => Boolean(point));
  const breached = riskPoints.some((point) => point.utilization > 1);
  const warning = !breached && riskPoints.some((point) => point.utilization >= 0.8);
  const availableFunds = selectedBot ? selectedBot.runtime?.lastKnownAccount?.availableFunds : summary?.availableEquity;
  const openPositions = selectedBot
    ? selectedBot.runtime?.lastKnownAccount?.positions?.length ?? (selectedBot.position === "FLAT" ? 0 : 1)
    : summary?.positionCount;
  const liveArmed = scopeBots.filter((bot) => bot.mode === "LIVE" && bot.safety?.allowLiveOrders === true).length;
  const finalizedRequired = scopeBots.length > 0 && scopeBots.every((bot) => bot.safety?.requireFinalizedCandles === true);
  const gapFailClosed = scopeBots.length > 0 && scopeBots.every((bot) => bot.safety?.pauseOnDataGap === true);
  const reconcileFailClosed = scopeBots.length > 0 && scopeBots.every((bot) => bot.safety?.pauseOnReconcileMismatch === true);
  const scopeLabel = selectedBot ? selectedBot.name : `All execution accounts · ${bots.length}`;
  const loading = botsQuery.query.isLoading || summaryQuery.query.isLoading;
  return (
    <Stack gap="5">
      <PageHeader
        title="Risk Controls"
        description="Persisted per-account utilization and server-enforced trading safeguards."
        actions={
          <>
            <StatusPill tone={loading ? "neutral" : breached ? "negative" : warning ? "warning" : "positive"}>{loading ? "SYNCING" : breached ? "RISK BREACHED" : warning ? "NEAR LIMIT" : "ALL LIMITS SAFE"}</StatusPill>
            <AppSelect
              ariaLabel="Select risk account scope"
              value={selectedBot?.id ?? "all"}
              width={{ base: "100%", md: "330px" }}
              options={[
                { value: "all", label: `All execution accounts · ${bots.length}` },
                ...bots.map((bot) => ({ value: bot.id, label: bot.name })),
              ]}
              onValueChange={setAccountScope}
            />
          </>
        }
      />
      <Flex bg="rgba(59,130,246,.06)" borderWidth="1px" borderColor="rgba(59,130,246,.2)" borderRadius="4px" p="4" gap="3" align={{ base: "start", md: "center" }} direction={{ base: "column", md: "row" }}>
        <Flex boxSize="34px" flexShrink="0" align="center" justify="center" borderRadius="4px" bg="rgba(59,130,246,.12)" color="status.info"><Box as={ShieldCheck} boxSize="4" /></Flex>
        <Box flex="1"><Text fontSize="sm" fontWeight="750">Risk is evaluated per execution account</Text><Text mt="1" fontSize="xs" color="fg.muted">{selectedBot ? "The budget below uses only this execution account's persisted state and configured server-side limits." : "Each budget below shows the highest utilization observed on any account. Independent paper ledgers are not merged into one artificial leverage limit."}</Text></Box>
        <StatusPill tone="info">{scopeLabel}</StatusPill>
      </Flex>
      <Grid templateColumns={{ base: "1fr 1fr", lg: "repeat(4,1fr)" }} gap="3">
        <MetricCard icon={ShieldCheck} label="Worst drawdown" value={drawdown ? `${drawdown.value === 0 ? "" : "-"}${drawdown.value.toFixed(2)}%` : "—"} detail={drawdown ? drawdown.source : "Waiting for risk state"} />
        <MetricCard icon={Gauge} label="Highest exposure" value={gross ? `${gross.value.toFixed(2)}x` : "—"} detail={gross ? `${gross.utilizationPct.toFixed(0)}% of ${gross.limit.toFixed(2)}x` : "Waiting for risk state"} />
        <MetricCard icon={CircleDollarSign} label="Available funds" value={availableFunds === undefined ? "—" : <Money value={availableFunds} />} detail={selectedBot ? "Selected execution account" : `${summary?.accountCount ?? 0} account snapshots`} />
        <MetricCard icon={Layers3} label="Open positions" value={openPositions === undefined ? "—" : String(openPositions)} detail={selectedBot ? selectedBot.position : "Across execution accounts"} />
      </Grid>
      <Grid templateColumns={{ base: "1fr", lg: "minmax(0,1.35fr) minmax(320px,.65fr)" }} gap="4" alignItems="start">
        <Panel icon={Gauge} title="Risk budget" action={<StatusPill tone={breached ? "negative" : warning ? "warning" : "positive"}>{breached ? "BREACHED" : warning ? "WARNING" : "SAFE"}</StatusPill>}>
          <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="3">
            <RiskBudgetCard icon={Gauge} label="Gross exposure" point={gross} />
            <RiskBudgetCard icon={ShieldCheck} label="Max drawdown" point={drawdown} />
            <RiskBudgetCard icon={Activity} label="Hard intraday loss" point={dailyLoss} />
            <RiskBudgetCard icon={WalletCards} label="Margin utilization" point={margin} />
          </Grid>
        </Panel>
        <Panel icon={Database} title="Safety authority" action={<StatusPill>SERVER</StatusPill>}>
          <Stack gap="2">
            <SafetyControl label="Persistent bot pause" detail="Survives UI and runtime restart" status="ENFORCED" tone="positive" />
            <SafetyControl label="Live order permission" detail={`${liveArmed} armed account${liveArmed === 1 ? "" : "s"}`} status={liveArmed > 0 ? "ARMED" : "DISABLED"} tone={liveArmed > 0 ? "negative" : "neutral"} />
            <SafetyControl label="Finalized candles" detail="Required by selected bot policies" status={finalizedRequired ? "REQUIRED" : "MIXED"} tone={finalizedRequired ? "positive" : "warning"} />
            <SafetyControl label="Data-gap fail closed" detail="Persistent pause on missing data" status={gapFailClosed ? "ENFORCED" : "MIXED"} tone={gapFailClosed ? "positive" : "warning"} />
            <SafetyControl label="Reconcile mismatch" detail="Persistent pause on broker drift" status={reconcileFailClosed ? "ENFORCED" : "MIXED"} tone={reconcileFailClosed ? "positive" : "warning"} />
          </Stack>
        </Panel>
      </Grid>
      <Panel icon={ShieldCheck} title="Per-account guardrails" action={<StatusPill>{scopeBots.length} {scopeBots.length === 1 ? "ACCOUNT" : "ACCOUNTS"}</StatusPill>}>
        <Grid templateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap="3">
          {snapshots.map((snapshot) => <AccountGuardCard key={snapshot.bot.id} snapshot={snapshot} />)}
        </Grid>
      </Panel>
    </Stack>
  );
}

type RiskSnapshot = {
  bot: BotRecord;
  gross: number;
  maxLeverage: number;
  drawdown: number;
  maxDrawdown: number;
  dailyLoss: number;
  maxDailyLoss: number;
  maxSessionCloseLoss: number;
  margin: number;
  openPositions: number;
};

type RiskPoint = { value: number; limit: number; utilization: number; utilizationPct: number; source: string };

function riskSnapshot(bot: BotRecord): RiskSnapshot {
  const account = bot.runtime?.lastKnownAccount;
  const equity = account?.equity;
  const highWater = bot.runtime?.highWaterEquity;
  const dayStart = bot.runtime?.dayStartEquity;
  const drawdown = equity !== undefined && highWater !== undefined && highWater > 0 ? Math.max(0, (highWater - equity) / highWater * 100) : 0;
  const dailyLoss = bot.pnlToday < 0 ? -bot.pnlToday / Math.max(1, dayStart ?? ((equity ?? 0) - bot.pnlToday)) * 100 : 0;
  const margin = equity && account?.marginUsed !== undefined ? account.marginUsed / equity * 100 : 0;
  return {
    bot,
    gross: Math.abs(bot.exposure),
    maxLeverage: bot.safety?.maxLeverage ?? 0,
    drawdown,
    maxDrawdown: (bot.safety?.maxDrawdownPct ?? 0) * 100,
    dailyLoss,
    maxDailyLoss: (bot.safety?.maxDailyLossPct ?? 0) * 100,
    maxSessionCloseLoss: (bot.safety?.maxSessionCloseLossPct ?? 0) * 100,
    margin,
    openPositions: account?.positions?.length ?? (bot.position === "FLAT" ? 0 : 1),
  };
}

function worstRisk(snapshots: RiskSnapshot[], metric: "gross" | "drawdown" | "dailyLoss" | "margin"): RiskPoint | undefined {
  const candidates = snapshots.map((snapshot) => {
    const value = snapshot[metric];
    const limit = metric === "gross" ? snapshot.maxLeverage : metric === "drawdown" ? snapshot.maxDrawdown : metric === "dailyLoss" ? snapshot.maxDailyLoss : 100;
    return { value, limit, utilization: limit > 0 ? value / limit : 0, utilizationPct: limit > 0 ? value / limit * 100 : 0, source: shortBotName(snapshot.bot) };
  });
  return candidates.sort((a, b) => b.utilization - a.utilization)[0];
}

function RiskBudgetCard({ icon: Icon, label, point }: { icon: LucideIcon; label: string; point?: RiskPoint }) {
  const tone = !point ? "neutral" : point.utilization > 1 ? "negative" : point.utilization >= 0.8 ? "warning" : "positive";
  return (
    <Box p="4" borderWidth="1px" borderColor="border.default" borderRadius="4px" bg="bg.subtle">
      <Flex justify="space-between" align="center" gap="3"><HStack gap="2"><Flex boxSize="28px" align="center" justify="center" borderRadius="4px" bg="bg.panel"><Box as={Icon} boxSize="3.5" color="fg.muted" /></Flex><Text fontSize="xs" fontWeight="800">{label}</Text></HStack><StatusPill tone={tone}>{!point ? "WAITING" : point.utilization > 1 ? "BREACHED" : point.utilization >= 0.8 ? "WARNING" : "SAFE"}</StatusPill></Flex>
      <Flex mt="4" justify="space-between" align="baseline" gap="3"><Text fontFamily="mono" fontSize="lg" fontWeight="850">{point ? point.value.toFixed(2) : "—"}</Text><Text fontFamily="mono" fontSize="10px" color="fg.muted">limit {point ? point.limit.toFixed(2) : "—"}</Text></Flex>
      <Box mt="2"><DataBar value={point?.value ?? 0} max={point?.limit ?? 1} tone={tone === "negative" ? "negative" : tone === "warning" ? "warning" : "positive"} /></Box>
      <Flex mt="2.5" justify="space-between" gap="3"><Text fontSize="10px" color="fg.muted">{point?.source ?? "Waiting for account state"}</Text><Text fontSize="10px" fontWeight="750">{point ? `${point.utilizationPct.toFixed(0)}% used` : "—"}</Text></Flex>
    </Box>
  );
}

function SafetyControl({ label, detail, status, tone }: { label: string; detail: string; status: string; tone: "positive" | "negative" | "warning" | "neutral" }) {
  return <Flex p="3" bg="bg.subtle" borderRadius="4px" align="center" gap="3"><Flex boxSize="28px" flexShrink="0" align="center" justify="center" borderRadius="4px" bg="bg.panel"><Box as={ShieldCheck} boxSize="3.5" color="fg.muted" /></Flex><Box flex="1" minW="0"><Text fontSize="xs" fontWeight="700">{label}</Text><Text mt=".5" fontSize="9px" color="fg.subtle" truncate>{detail}</Text></Box><StatusPill tone={tone}>{status}</StatusPill></Flex>;
}

function AccountGuardCard({ snapshot }: { snapshot: RiskSnapshot }) {
  const bot = snapshot.bot;
  return (
    <Box p="4" borderWidth="1px" borderColor="border.default" borderRadius="4px" bg="bg.subtle">
      <Flex justify="space-between" align="start" gap="3"><Box minW="0"><Text fontSize="sm" fontWeight="800" truncate>{shortBotName(bot)}</Text><Text mt="1" fontSize="10px" fontFamily="mono" color="fg.subtle" truncate>{bot.strategy}</Text></Box><HStack gap="1.5"><StatusPill tone={bot.mode === "LIVE" ? "negative" : "warning"}>{bot.mode}</StatusPill><StatusPill tone={bot.status === "RUNNING" ? "positive" : "neutral"}>{bot.status}</StatusPill></HStack></Flex>
      <Grid mt="3" templateColumns={{ base: "1fr 1fr", md: "repeat(3,1fr)" }} gap="2">
        <GuardFact label="Max leverage" value={`${snapshot.maxLeverage.toFixed(2)}x`} />
        <GuardFact label="Max drawdown" value={`${snapshot.maxDrawdown.toFixed(2)}%`} />
        <GuardFact label="Hard intraday loss" value={`${snapshot.maxDailyLoss.toFixed(2)}%`} />
        {snapshot.maxSessionCloseLoss > 0 && <GuardFact label="Session-close loss" value={`${snapshot.maxSessionCloseLoss.toFixed(2)}%`} />}
        <GuardFact label="Orders / day" value={`${bot.runtime?.ordersToday ?? 0} / ${bot.safety?.maxOrdersPerDay ?? "—"}`} />
        <GuardFact label="Live orders" value={bot.safety?.allowLiveOrders ? "ARMED" : "DISABLED"} />
        <GuardFact label="Open positions" value={String(snapshot.openPositions)} />
      </Grid>
      <Flex mt="3" justify="space-between" align="center" gap="3"><Text fontSize="10px" color="fg.muted">Snapshot {formatRuntimeTimestamp(bot.runtime?.lastKnownAccount?.asOfMs)}</Text><Button asChild size="xs" variant="ghost"><Link to={`/bots/${bot.id}`}><Box as={Bot} boxSize="3.5" />View bot</Link></Button></Flex>
    </Box>
  );
}

function GuardFact({ label, value }: { label: string; value: string }) {
  return <Box p="2.5" borderRadius="4px" bg="bg.panel"><Text fontSize="9px" color="fg.subtle" textTransform="uppercase" letterSpacing=".04em">{label}</Text><Text mt="1" fontFamily="mono" fontSize="xs" fontWeight="800">{value}</Text></Box>;
}

function shortBotName(bot: BotRecord): string {
  return bot.strategy === "xau-multi-edge-portfolio-v1" ? "XAU Multi-Edge Portfolio" : bot.strategy === "xau-session-momentum-v1" ? "XAU Session Momentum" : bot.name;
}
